import React, { useEffect, useState } from 'react';
import cn from 'classnames';
import Image from '@components/Image';
import { BaseModal } from '@repo/components/modals';
import Action from '@components/Button/Action';
import styles from './UpdateLinkedInIntegrationCallout.module.scss';
import { useDispatch, useSelector } from 'react-redux';
import { archiveIntegration, updateIntegrationVersion } from '@repo/redux/actions/integrations';
import { LoaderIcon } from '@repo/icons';
import LinkedinModal from '@components/Integrations/Modal/LinkedIn';
import { trackConnectionScreen } from '@components/Integrations/utils';
import { useIsElectron } from '@repo/hooks';
import { selectIntegrationByPlatform } from '@repo/redux/reducers/user/integrations';

const linkedin = '/img/settings/linkedin-dark.png';
const linkedinLight = '/img/settings/linkedin-light.png';

const targetVersion = 2;

export default function UpdateLinkedInIntegrationCallout() {
  const dispatch = useDispatch();
  const isElectron = useIsElectron();
  const linkedinIntegration = useSelector(selectIntegrationByPlatform('linkedin'));
  const [isUpdating, setIsUpdating] = useState(false);
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [currentVersion, setCurrentVersion] = useState(-1);
  const [alreadyUpgraded, setAlreadyUpgraded] = useState(false);

  const upgrade = async () => {
    setIsUpdating(true);
    dispatch(updateIntegrationVersion('linkedin', targetVersion));
  };

  const optOut = () => {
    dispatch(archiveIntegration(linkedinIntegration));
  };

  useEffect(() => {
    if (linkedinIntegration?.isLoaded && currentVersion === -1) {
      if (linkedinIntegration.metadata?.version === targetVersion) {
        setAlreadyUpgraded(true);
        return;
      }

      setCurrentVersion(linkedinIntegration.metadata?.version);
    }
  }, [linkedinIntegration]);

  useEffect(() => {
    if (
      isElectron &&
      linkedinIntegration &&
      linkedinIntegration.metadata?.version === targetVersion &&
      currentVersion !== targetVersion
    ) {
      setShowSyncModal(true);
      setCurrentVersion(targetVersion);
    }
  }, [linkedinIntegration, currentVersion]);

  if (alreadyUpgraded) {
    return null;
  }

  if (showSyncModal) {
    return (
      <LinkedinModal
        onClose={() => setShowSyncModal(false)}
        renderLocation="settings"
        trackConnection={trackConnectionScreen}
        autoSync
      />
    );
  }

  if (
    !linkedinIntegration ||
    linkedinIntegration.isFetching ||
    linkedinIntegration.metadata?.version === targetVersion
  ) {
    return null;
  }

  return (
    <BaseModal
      className={cn(styles.modalWrapper, 'modal-large')}
      centered
      isOpen={true}
      onRequestClose={() => {}}
    >
      <div className={styles.content}>
        <Image className={styles.image} width={207} dark={linkedin} light={linkedinLight} />
        <div className={styles.content}>
          <h4>Enhanced LinkedIn Integration</h4>
          <p className="text-secondary">
            We've launched a more powerful LinkedIn integration, which now includes curated LinkedIn
            posts in your Clay Home feed!
          </p>
          <p className="text-secondary">
            LinkedIn sync will stop working if you choose not to upgrade.{' '}
            <a target="_blank" href="https://go.clay.earth/linkedin-posts">
              Learn more
            </a>
            .
          </p>
          <div className={styles.actions}>
            <Action className={styles.btn} onClick={upgrade} disabled={isUpdating}>
              {isUpdating ? <LoaderIcon width="12" /> : 'Continue'}
            </Action>
            <Action className={styles.mutedBtn} onClick={optOut} disabled={isUpdating}>
              Stop syncing
            </Action>
          </div>
        </div>
      </div>
    </BaseModal>
  );
}
